import React from "react";

import { Editor } from "~/components/code/editor";
import { IconCopyOutlined } from "~/components/icon";
import { cx } from "~/tools/class-variance";
import { copyToClipboard } from "~/tools/copy-to-clipboard";

import { useExportNodeQuery } from "../../queries";
import { Button } from "../react/buttons/Button";
import { GenericErrorMessage } from "../react/error/GenericErrorMessage";
import { LoadingSpinner } from "../react/loading-spinner/LoadingSpinner";
import { DONT_AUTO_DISMISS, notification } from "../react/notification/Notification";
import { Tooltip } from "../react/tooltip/Tooltip";

interface DefinitionTabContentProps {
	className?: string;
	monitorId: number;
	title: string;
}

export const DefinitionTabContent: React.FC<DefinitionTabContentProps> = ({ className, monitorId, title }) => {
	const { data: yaml = "", isLoading, error, refetch } = useExportNodeQuery(monitorId);

	if (isLoading) {
		return <LoadingSpinner className="h-full" size="large" />;
	}

	if (error != null) {
		return (
			<GenericErrorMessage
				className="h-full"
				error={error}
				highlightText="Failed"
				onRetry={() => void refetch()}
				text={`Failed to load monitor definition for '${title}`}
			/>
		);
	}

	return (
		<div className={cx("group relative", className)}>
			<Tooltip placement="top" title="Copy to clipboard">
				<Button
					className="absolute top-2 right-2 z-1 flex items-center justify-center transition-opacity group-hover:opacity-100"
					icon={<IconCopyOutlined />}
					onClick={() => {
						const [failure, success] = copyToClipboard(yaml, "monitor definition");

						if (failure == null) {
							notification.success({ message: success });
						} else {
							notification.error({
								message: failure,
								duration: DONT_AUTO_DISMISS,
							});
						}
					}}
					shape="circle"
					size="large"
				/>
			</Tooltip>

			<Editor height="100%" language="yaml" readOnly value={yaml} />
		</div>
	);
};
